import { useState } from "react";
import { api, mxn, fmtDate } from "@/lib/api";
import { Btn, ConfirmDialog } from "@/components/Kit";
import { PaymentModal } from "@/components/PaymentModal";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Banknote, Download } from "lucide-react";

export function ConciliacionPagos({ banks, onCreateBank }) {
  const [file, setFile] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [applied, setApplied] = useState({}); // invoice_id -> true
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [payTarget, setPayTarget] = useState(null);

  const process = async () => {
    if (!file) return toast.error("Selecciona un archivo Excel");
    setLoading(true);
    setData(null);
    setSel(new Set());
    setApplied({});
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/reconciliation/payments/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData(res);
      if (res.matched.length === 0) toast.info("No se encontraron coincidencias aplicables");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo leer el archivo");
    } finally {
      setLoading(false);
    }
  };

  const selectableRows = () => (data?.matched || []).filter((m) => !applied[m.invoice_id]);

  const toggle = (id) =>
    setSel((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleAll = (checked) =>
    setSel(checked ? new Set(selectableRows().map((m) => m.invoice_id)) : new Set());

  const selectedRows = () => selectableRows().filter((m) => sel.has(m.invoice_id));

  const applyBulk = async () => {
    setConfirmOpen(false);
    setApplying(true);
    let ok = 0, fail = 0;
    const newApplied = { ...applied };
    for (const m of selectedRows()) {
      try {
        await api.post("/payments", {
          invoice_id: m.invoice_id, amount: m.amount, method: m.method || "Transferencia",
          date: m.date || "", bank: m.bank || "", reference: "", notes: "",
        });
        newApplied[m.invoice_id] = true;
        ok++;
      } catch (e) {
        fail++;
      }
    }
    setApplied(newApplied);
    setSel(new Set());
    setApplying(false);
    toast.success(`${ok} pago(s) aplicado(s)${fail ? `, ${fail} con error` : ""}`);
  };

  const exportExcel = async () => {
    if (!data) return;
    const rows = [
      ...data.matched.map((m) => ({
        folio: m.folio, invoice_number: m.invoice_number, client_name: m.client_name,
        amount: m.amount, date: m.date, method: m.method, bank: m.bank,
        resultado: applied[m.invoice_id] ? "Pago aplicado" : "Coincidencia (no aplicado)",
      })),
      ...data.unmatched.map((u) => ({
        folio: u.folio, invoice_number: "", client_name: u.excel_cliente,
        amount: u.amount, date: u.date, method: u.method, bank: u.bank,
        resultado: `Sin coincidencia: ${u.reason}`,
      })),
    ];
    try {
      const res = await api.post("/reconciliation/payments/export", { rows, filename: data.filename }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "conciliacion_pagos.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo exportar");
    }
  };

  return (
    <div data-testid="conciliacion-pagos">
      <div className="border border-[#465B7F]/25 bg-[#465B7F]/5 px-4 py-3 mb-6 font-mono-data text-xs text-[#465B7F]">
        Sube un Excel de pagos (columnas <b>factura, nombre, total, PAG/CRED, FORM PAG, banco, FECH PAG, SALDO</b>). Se cruza contra facturas <b>sin pago aplicado</b> (solo renglones marcados como pagados). El pago se registra por <b>total − saldo</b>. Nada se aplica sin tu confirmación.
      </div>

      <div className="border border-[#E3E8EE] bg-white p-4 mb-6 flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 cursor-pointer border border-[#465B7F] text-[#465B7F] px-3 py-2 text-sm hover:bg-[#465B7F] hover:text-white transition-colors" data-testid="pay-upload-label">
          <Upload size={15} /> {file ? "Cambiar archivo" : "Seleccionar Excel"}
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="pay-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        {file && <span className="font-mono-data text-xs text-[#5B6672] inline-flex items-center gap-1"><FileSpreadsheet size={13} /> {file.name}</span>}
        <Btn onClick={process} disabled={loading || !file} data-testid="pay-process-button">
          {loading ? "Procesando…" : "Procesar"}
        </Btn>
        {data && (
          <button onClick={exportExcel} data-testid="pay-export-button"
            className="ml-auto inline-flex items-center gap-1.5 border border-[#465B7F] text-[#465B7F] px-3 py-2 text-xs uppercase tracking-wider hover:bg-[#465B7F] hover:text-white transition-colors">
            <Download size={13} /> Exportar Excel
          </button>
        )}
      </div>

      {data && (
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pay-summary">
            <div className="border border-[#49AC9A]/40 bg-white p-4">
              <div className="overline">Coincidencias</div>
              <div className="font-display font-extrabold text-3xl mt-1 text-[#2f7a6c]">{data.totals.matched}</div>
              <div className="font-mono-data text-sm mt-1">{mxn(data.totals.matched_total)}</div>
            </div>
            <div className="border border-[#ED1F26]/40 bg-white p-4">
              <div className="overline">Sin coincidencia</div>
              <div className="font-display font-extrabold text-3xl mt-1 text-[#ED1F26]">{data.totals.unmatched}</div>
            </div>
          </div>

          {/* Coincidencias */}
          <div className="border border-[#E3E8EE] bg-white">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E3E8EE]">
              <CheckCircle2 size={16} className="text-[#49AC9A]" />
              <span className="font-display font-bold text-[#16202B]">Coincidencias encontradas</span>
              <div className="ml-auto flex items-center gap-3">
                <Btn onClick={() => setConfirmOpen(true)} disabled={applying || selectedRows().length === 0} data-testid="pay-apply-button">
                  <Banknote size={14} /> {applying ? "Aplicando…" : `Aplicar (${selectedRows().length})`}
                </Btn>
              </div>
            </div>
            {data.matched.length === 0 ? (
              <p className="font-mono-data text-sm text-[#5B6672] py-6 text-center">Ninguna factura pendiente coincide con el archivo.</p>
            ) : (
              <table className="w-full" data-testid="pay-matched-table">
                <thead><tr className="border-b border-[#E3E8EE] text-left">
                  <th className="px-4 py-3 w-8">
                    <input type="checkbox" data-testid="pay-select-all" className="h-4 w-4 accent-[#465B7F]"
                      checked={selectableRows().length > 0 && sel.size === selectableRows().length}
                      onChange={(e) => toggleAll(e.target.checked)} />
                  </th>
                  <Th>Folio Excel</Th><Th>Factura</Th><Th>Cliente</Th><Th>Método</Th><Th>Banco</Th><Th>Fecha pago</Th><Th className="text-right">Monto</Th><Th>Estado</Th>
                </tr></thead>
                <tbody>
                  {data.matched.map((m) => {
                    const done = applied[m.invoice_id];
                    return (
                      <tr key={m.invoice_id + m.row} data-testid={`pay-matched-${m.folio_digits}`}
                        className={`border-b border-[#E3E8EE] last:border-0 ${done ? "bg-[#49AC9A]/10" : "hover:bg-[#E9EEF3]"}`}>
                        <td className="px-4 py-3">
                          <input type="checkbox" className="h-4 w-4 accent-[#465B7F]" disabled={done}
                            checked={sel.has(m.invoice_id)} onChange={() => toggle(m.invoice_id)}
                            data-testid={`pay-check-${m.folio_digits}`} />
                        </td>
                        <Td>{m.folio}</Td>
                        <Td className="text-[#16202B] font-medium">{m.invoice_number}</Td>
                        <Td>{m.client_name || "—"}</Td>
                        <Td>{m.method}</Td>
                        <Td data-testid={`pay-bank-${m.folio_digits}`}>{m.bank || "—"}</Td>
                        <Td data-testid={`pay-date-${m.folio_digits}`}>{m.date ? fmtDate(m.date) : "—"}</Td>
                        <Td className="text-right text-[#16202B]">{mxn(m.amount)}</Td>
                        <Td>
                          {done ? (
                            <span className="text-xs uppercase tracking-wider px-1.5 py-0.5 bg-[#49AC9A]/15 text-[#2f7a6c] border border-[#49AC9A]/30">Aplicado</span>
                          ) : (
                            <button className="text-xs text-[#465B7F] hover:underline" data-testid={`pay-single-${m.folio_digits}`}
                              onClick={() => setPayTarget(m)}>Aplicar / editar…</button>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Sin coincidencia */}
          <div className="border border-[#ED1F26]/30 bg-white">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E3E8EE]">
              <AlertTriangle size={16} className="text-[#ED1F26]" />
              <span className="font-display font-bold text-[#16202B]">Sin coincidencia</span>
              <span className="ml-auto font-mono-data text-xs text-[#5B6672]">{data.unmatched.length} renglón(es)</span>
            </div>
            {data.unmatched.length === 0 ? (
              <p className="font-mono-data text-sm text-[#5B6672] py-6 text-center">Todos los renglones cruzaron con una factura.</p>
            ) : (
              <table className="w-full" data-testid="pay-unmatched-table">
                <thead><tr className="border-b border-[#E3E8EE] text-left">
                  <Th>Folio Excel</Th><Th>Cliente</Th><Th className="text-right">Monto</Th><Th>Motivo</Th>
                </tr></thead>
                <tbody>
                  {data.unmatched.map((u, i) => (
                    <tr key={u.row + "-" + i} className="border-b border-[#E3E8EE] last:border-0 bg-[#FBEAEA]">
                      <Td className="text-[#8f2d2d]">{u.folio || "—"}</Td>
                      <Td>{u.excel_cliente || "—"}</Td>
                      <Td className="text-right">{mxn(u.amount)}</Td>
                      <Td className="text-[#8f2d2d]">{u.reason}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={applyBulk}
        title="Aplicar pagos"
        message={`Se registrarán ${selectedRows().length} pago(s) por un total de ${mxn(selectedRows().reduce((s, m) => s + m.amount, 0))} sobre las facturas seleccionadas. Esta acción actualiza los registros. ¿Deseas continuar?`}
        confirmLabel="Sí, aplicar pagos"
        testid="confirm-apply-payments-button"
      />

      <PaymentModal
        open={!!payTarget}
        onClose={() => setPayTarget(null)}
        target={payTarget ? { id: payTarget.invoice_id, number: payTarget.invoice_number, balance: payTarget.amount, type: "factura" } : null}
        prefill={payTarget ? { date: payTarget.date, bank: payTarget.bank, method: payTarget.method } : null}
        banks={banks}
        onCreateBank={onCreateBank}
        onSaved={() => {
          if (payTarget) setApplied((p) => ({ ...p, [payTarget.invoice_id]: true }));
          setPayTarget(null);
        }}
      />
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`overline font-normal px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`font-mono-data text-sm text-[#5C5A55] px-4 py-3 ${className}`}>{children}</td>;
}
